import Alert from '@risk-smart/themed-cloudscape-components/alert';
import ContentLayout from '@risk-smart/themed-cloudscape-components/content-layout';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Breadcrumbs from '@risksmart-app/components/src/breadcrumbs';
import PageHeader from '@risksmart-app/components/src/page-header';
import { useBaseTracking } from '@risksmart-app/components/src/segment';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { ErrorBoundary } from '@sentry/react';
import type { ParseKeys } from 'i18next';
import type { FC, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { I18nSummaryHelpContent } from 'src/components/help-panel/SummaryHelpContent';
import { useHelpStore } from 'src/components/help-panel/useHelpStore';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

import { ChatButton } from '../components/chat/ChatButton';
import { AuthenticatedAppLayout } from './AuthenticatedAppLayout';

type Meta = {
  /** Used to set `window.title` where the page title itself contains sensitive information */
  title?: string;
};
interface Props {
  title?: string;
  /** @deprecated Use {@link Props.meta} instead */
  pageTitle?: string;
  meta?: Meta;
  actions?: ReactNode;
  children?: ReactNode | ReactNode[];
  counter?: string;
  secondary?: ReactNode;
  panelContent?: ReactNode;
  protected?: boolean;
  helpTranslationKey?: ParseKeys<'common'>;
}

const PageLayout: FC<Props> = ({
  title,
  meta,
  pageTitle,
  counter,
  actions,
  secondary,
  panelContent,
  protected: isProtected = true,
  children,
  helpTranslationKey,
}) => {
  useBaseTracking();
  const [toolsContent, setToolsContent] = useTools();

  const { setContentId, getHasHelpContent } = useHelpStore();
  const toggleHelp = () => {
    setContentId(null);
    setToolsContent(toolsContent === 'help' ? undefined : 'help');
  };
  const {
    hasPermission: hasUpdateTaxonomyPermission,
    loading: isLoadingUpdateTaxonomyPermission,
  } = useHasPermissionQuery('update:taxonomy');
  const showHelp =
    !isLoadingUpdateTaxonomyPermission &&
    (getHasHelpContent() || hasUpdateTaxonomyPermission);
  const isChatEnabled = useIsFeatureVisibleToOrg('chat');
  const visibleTitle = title || meta?.title || pageTitle;
  const metaTitle = meta?.title || pageTitle || title;
  const page = (
    <>
      <Helmet>
        <title data-amp-mask>{metaTitle}</title>
      </Helmet>
      {helpTranslationKey && (
        <I18nSummaryHelpContent translationKey={helpTranslationKey} />
      )}

      <ContentLayout
        disableOverlap
        defaultPadding
        headerVariant={'high-contrast'}
        header={
          <div className={'print:hidden'}>
            <div className={'flex items-center flex-wrap mx-auto'}>
              <div className={'block w-full'}>
                <Breadcrumbs />
                <SpaceBetween size={'m'}>
                  <PageHeader
                    counter={counter}
                    actions={
                      <SpaceBetween direction={'horizontal'} size={'xs'}>
                        {isChatEnabled && <ChatButton />}
                        {actions}
                      </SpaceBetween>
                    }
                    showHelp={showHelp}
                    onShowHelpClick={toggleHelp}
                  >
                    {visibleTitle}
                  </PageHeader>
                  {secondary}
                </SpaceBetween>
              </div>
            </div>
          </div>
        }
      >
        <div className={'py-5'}>
          <ErrorBoundary
            onError={(error) => console.error(error)}
            fallback={
              <Alert header={'Error'} type={'error'}>
                {'An error has occurred'}
              </Alert>
            }
          >
            <SpaceBetween size={'m'}>{children}</SpaceBetween>
          </ErrorBoundary>
        </div>
      </ContentLayout>
    </>
  );

  return isProtected ? (
    <AuthenticatedAppLayout panelContent={panelContent}>
      {page}
    </AuthenticatedAppLayout>
  ) : (
    <>{children}</>
  );
};

export default PageLayout;
